import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { HeatmapTooltipCard } from './HeatmapTooltipCard.jsx';
import { buildHeatmapHeaderSpread, normalizeHeaderToRichTooltip } from './heatmapHeaderModel.js';
import './charts.css';

// Surface keys. Cells keep their original unprefixed `${portfolioId}:${dimensionKey}`
// id (so existing selectors/behavior are untouched); headers use distinct prefixes.
const colSurfaceId = (key) => `col:${key}`;
const rowSurfaceId = (portfolioId) => `row:${portfolioId}`;

export function MissionImpactHeatmap({
  rows = [],
  columns = [],
  cells = [],
  cellSize = 48,
  gap = 4,
  ariaLabel,
}) {
  const rootRef = useRef(null);
  const scrollRef = useRef(null);
  const buttonRefs = useRef(new Map());
  const drillLinkRef = useRef(null);
  const tooltipRef = useRef(null);
  const hoverCloseTimerRef = useRef(null);
  const [activeSurfaceId, setActiveSurfaceId] = useState(null);
  const [pinnedSurfaceId, setPinnedSurfaceId] = useState(null);
  const [activeOrigin, setActiveOrigin] = useState(null);
  const [anchorRect, setAnchorRect] = useState(null);

  const cellMap = useMemo(
    () => new Map(cells.map((cell) => [`${cell.portfolioId}:${cell.dimensionKey}`, cell])),
    [cells],
  );
  const columnMap = useMemo(() => new Map(columns.map((c) => [c.key, c])), [columns]);
  const rowMap = useMemo(() => new Map(rows.map((r) => [r.portfolioId, r])), [rows]);

  const activeId = pinnedSurfaceId || activeSurfaceId;

  // Resolve the active surface key into either a cell (normalized downstream) or a
  // prebuilt header RichTooltip payload.
  const activeSurface = useMemo(() => {
    if (!activeId) return null;
    if (activeId.startsWith('col:')) {
      const key = activeId.slice(4);
      const column = columnMap.get(key);
      if (!column) return null;
      const spread = buildHeatmapHeaderSpread(cells, 'column', key);
      return {
        kind: 'header',
        payload: normalizeHeaderToRichTooltip({
          axis: 'column',
          key,
          label: column.label,
          fullLabel: column.fullLabel,
          whatIs: column.whatIs,
          scaleNote: column.scaleNote,
          spread,
          trust: column.trust,
          tone: column.tone,
        }),
      };
    }
    if (activeId.startsWith('row:')) {
      const key = activeId.slice(4);
      const row = rowMap.get(key);
      if (!row) return null;
      const spread = buildHeatmapHeaderSpread(cells, 'row', key);
      return {
        kind: 'header',
        payload: normalizeHeaderToRichTooltip({
          axis: 'row',
          key,
          label: row.label,
          fullLabel: row.fullLabel,
          whatIs: row.whatIs,
          scaleNote: row.scaleNote,
          spread,
          trust: row.trust,
          tone: row.tone,
        }),
      };
    }
    const cell = cellMap.get(activeId);
    return cell ? { kind: 'cell', cell } : null;
  }, [activeId, cellMap, columnMap, rowMap, cells]);

  const rootBounds = rootRef.current?.getBoundingClientRect();
  const containerBounds = {
    width: typeof window !== 'undefined' ? window.innerWidth : 1280,
    height: typeof window !== 'undefined' ? window.innerHeight : 900,
    offsetLeft: rootBounds?.left || 0,
    offsetTop: rootBounds?.top || 0,
  };

  const closeTooltip = useCallback(
    ({ restoreFocus = true } = {}) => {
      const focusId = pinnedSurfaceId || activeSurfaceId;
      setPinnedSurfaceId(null);
      setActiveSurfaceId(null);
      setActiveOrigin(null);
      if (!restoreFocus || !focusId) return;
      const button = buttonRefs.current.get(focusId);
      if (button) {
        requestAnimationFrame(() => button.focus());
      }
    },
    [activeSurfaceId, pinnedSurfaceId],
  );

  const syncAnchorRect = useCallback(() => {
    if (!activeId) {
      setAnchorRect(null);
      return;
    }
    const button = buttonRefs.current.get(activeId);
    const root = rootRef.current;
    if (!button || !root) {
      setAnchorRect(null);
      return;
    }
    const buttonRect = button.getBoundingClientRect();
    const nextRootBounds = root.getBoundingClientRect();
    setAnchorRect({
      left: buttonRect.left - nextRootBounds.left,
      top: buttonRect.top - nextRootBounds.top,
      width: buttonRect.width,
      height: buttonRect.height,
    });
  }, [activeId]);

  useEffect(() => {
    function handleOutsidePointerDown(event) {
      if (!pinnedSurfaceId) return;
      if (rootRef.current?.contains(event.target)) return;
      closeTooltip({ restoreFocus: false });
    }
    document.addEventListener('pointerdown', handleOutsidePointerDown);
    return () => document.removeEventListener('pointerdown', handleOutsidePointerDown);
  }, [closeTooltip, pinnedSurfaceId]);

  useLayoutEffect(() => {
    syncAnchorRect();
  }, [syncAnchorRect]);

  useEffect(() => {
    const handleLayoutChange = () => syncAnchorRect();
    window.addEventListener('resize', handleLayoutChange);
    const scroller = scrollRef.current;
    scroller?.addEventListener('scroll', handleLayoutChange, { passive: true });
    return () => {
      window.removeEventListener('resize', handleLayoutChange);
      scroller?.removeEventListener('scroll', handleLayoutChange);
    };
  }, [syncAnchorRect]);

  useEffect(() => {
    if (cells.length === 0) {
      setPinnedSurfaceId(null);
      setActiveSurfaceId(null);
      setActiveOrigin(null);
    }
  }, [cells.length]);

  const clearTransientTooltip = useCallback(() => {
    if (hoverCloseTimerRef.current) {
      clearTimeout(hoverCloseTimerRef.current);
      hoverCloseTimerRef.current = null;
    }
    if (pinnedSurfaceId || activeOrigin !== 'hover') return;
    setActiveSurfaceId(null);
    setActiveOrigin(null);
  }, [activeOrigin, pinnedSurfaceId]);

  const cancelHoverClose = useCallback(() => {
    if (!hoverCloseTimerRef.current) return;
    clearTimeout(hoverCloseTimerRef.current);
    hoverCloseTimerRef.current = null;
  }, []);

  const scheduleTransientTooltipClose = useCallback(
    (surfaceId) => {
      if (pinnedSurfaceId || activeOrigin !== 'hover') return;
      cancelHoverClose();
      hoverCloseTimerRef.current = setTimeout(() => {
        hoverCloseTimerRef.current = null;
        setActiveSurfaceId((current) => (current === surfaceId ? null : current));
        setActiveOrigin((current) =>
          activeSurfaceId === surfaceId && current === 'hover' ? null : current,
        );
      }, 180);
    },
    [activeSurfaceId, activeOrigin, cancelHoverClose, pinnedSurfaceId],
  );

  const activateSurface = useCallback(
    (surfaceId, origin) => {
      cancelHoverClose();
      if (pinnedSurfaceId) {
        if (pinnedSurfaceId === surfaceId) {
          setActiveSurfaceId(surfaceId);
          setActiveOrigin('pinned');
        }
        return;
      }
      setActiveSurfaceId(surfaceId);
      setActiveOrigin(origin);
    },
    [cancelHoverClose, pinnedSurfaceId],
  );

  const handleSurfaceBlur = useCallback(() => {
    if (pinnedSurfaceId) return;
    setTimeout(() => {
      if (tooltipRef.current?.contains(document.activeElement)) return;
      if (!rootRef.current?.contains(document.activeElement)) {
        setActiveSurfaceId(null);
        setActiveOrigin(null);
      }
    }, 0);
  }, [pinnedSurfaceId]);

  function handleTooltipMouseLeave(event) {
    if (pinnedSurfaceId || activeOrigin !== 'hover') return;
    const nextTarget = event.relatedTarget;
    if (!(nextTarget instanceof Node)) {
      clearTransientTooltip();
      return;
    }
    if (tooltipRef.current?.contains(nextTarget)) return;
    if (buttonRefs.current.get(activeId)?.contains(nextTarget)) return;
    if (
      nextTarget instanceof HTMLElement &&
      nextTarget.closest('[data-testid^="heatmap-cell-"],[data-heatmap-axis]')
    )
      return;
    clearTransientTooltip();
  }

  // Shared hover/focus props for any trigger surface (cell or header).
  const triggerHandlers = (surfaceId) => ({
    onMouseEnter: () => activateSurface(surfaceId, 'hover'),
    onMouseLeave: (event) => {
      if (event.relatedTarget instanceof Node && tooltipRef.current?.contains(event.relatedTarget))
        return;
      scheduleTransientTooltipClose(surfaceId);
    },
    onFocus: () => activateSurface(surfaceId, 'focus'),
    onBlur: handleSurfaceBlur,
  });

  const pinSurface = (surfaceId) => {
    setActiveSurfaceId(surfaceId);
    setPinnedSurfaceId(surfaceId);
    setActiveOrigin('pinned');
  };

  function renderAxisHeader(axis, { key, surfaceId, label, fullLabel, hasSummary, testId }) {
    const isActive = activeId === surfaceId;
    const isPinned = pinnedSurfaceId === surfaceId;
    return (
      <button
        key={key}
        ref={(node) => {
          if (node) buttonRefs.current.set(surfaceId, node);
          else buttonRefs.current.delete(surfaceId);
        }}
        type="button"
        className={`heatmap-grid-axis heatmap-grid-axis-${axis}${isActive ? ' is-active' : ''}${isPinned ? ' is-pinned' : ''}`}
        data-testid={testId}
        data-heatmap-axis={axis}
        aria-label={`${fullLabel || label}, ${axis} header`}
        aria-describedby={
          isActive && !isPinned
            ? hasSummary
              ? 'heatmap-tooltip-summary'
              : 'heatmap-tooltip-title'
            : undefined
        }
        aria-expanded={isPinned ? 'true' : 'false'}
        aria-haspopup="dialog"
        {...triggerHandlers(surfaceId)}
        onClick={() => pinSurface(surfaceId)}
        onKeyDown={(event) => {
          if (event.key === 'Escape') {
            event.preventDefault();
            closeTooltip();
          }
        }}
      >
        {label}
      </button>
    );
  }

  return (
    <div
      ref={rootRef}
      className="heatmap"
      role="group"
      aria-label={ariaLabel || 'Mission impact heatmap'}
      data-testid="mission-heatmap"
      onKeyDownCapture={(event) => {
        if (event.key === 'Escape' && (pinnedSurfaceId || activeSurfaceId)) {
          event.preventDefault();
          closeTooltip();
        }
      }}
    >
      <div className="heatmap-scroll" ref={scrollRef}>
        <div
          className="heatmap-grid"
          style={{
            '--heatmap-columns': columns.length,
            '--heatmap-row-gutter': '172px',
            '--heatmap-cell-size': `${cellSize}px`,
            '--heatmap-gap': `${gap}px`,
          }}
        >
          <div className="heatmap-corner" aria-hidden />
          {columns.map((column) =>
            renderAxisHeader('column', {
              key: column.key,
              surfaceId: colSurfaceId(column.key),
              label: column.label,
              fullLabel: column.fullLabel,
              hasSummary: Boolean(column.whatIs),
              testId: `heatmap-column-${column.key}`,
            }),
          )}

          {rows.map((row, rowIndex) => (
            <div className="heatmap-grid-row" key={row.portfolioId}>
              {renderAxisHeader('row', {
                key: row.portfolioId,
                surfaceId: rowSurfaceId(row.portfolioId),
                label: row.label,
                fullLabel: row.fullLabel,
                hasSummary: Boolean(row.whatIs),
                testId: `heatmap-row-${row.portfolioId}`,
              })}

              {columns.map((column, colIndex) => {
                const cellId = `${row.portfolioId}:${column.key}`;
                const cell = cellMap.get(cellId);
                const isActive = activeId === cellId;
                const isPinned = pinnedSurfaceId === cellId;

                if (!cell || cell.score == null) {
                  return <div key={cellId} className="heatmap-grid-cell is-empty" aria-hidden />;
                }

                return (
                  <button
                    key={cellId}
                    ref={(node) => {
                      if (node) buttonRefs.current.set(cellId, node);
                      else buttonRefs.current.delete(cellId);
                    }}
                    type="button"
                    className={`heatmap-grid-cell tone-${cell.tone}${isActive ? ' is-active' : ''}${isPinned ? ' is-pinned' : ''}`}
                    data-testid={`heatmap-cell-${row.portfolioId}-${column.key}`}
                    data-heatmap-surface="cell"
                    aria-label={`${cell.portfolioLabel} ${cell.dimensionLabel} score ${cell.score}`}
                    aria-describedby={isActive && !isPinned ? 'heatmap-tooltip-summary' : undefined}
                    aria-expanded={isPinned ? 'true' : 'false'}
                    aria-haspopup="dialog"
                    {...triggerHandlers(cellId)}
                    onClick={() => pinSurface(cellId)}
                    onKeyDown={(event) => {
                      if (
                        event.key === 'Tab' &&
                        !event.shiftKey &&
                        pinnedSurfaceId === cellId &&
                        drillLinkRef.current
                      ) {
                        event.preventDefault();
                        drillLinkRef.current.focus();
                      }
                      if (event.key === 'Escape') {
                        event.preventDefault();
                        closeTooltip();
                      }
                    }}
                    style={{
                      gridColumnStart: colIndex + 2,
                      gridRowStart: rowIndex + 2,
                    }}
                  >
                    <span className="heatmap-grid-cell-value">{cell.score}</span>
                  </button>
                );
              })}
            </div>
          ))}
        </div>
      </div>

      <HeatmapTooltipCard
        cell={activeSurface?.kind === 'cell' ? activeSurface.cell : null}
        payload={activeSurface?.kind === 'header' ? activeSurface.payload : null}
        surfaceKind={activeSurface?.kind === 'header' ? 'header' : 'cell'}
        open={Boolean(activeSurface)}
        pinned={Boolean(pinnedSurfaceId)}
        anchorRect={anchorRect}
        containerBounds={containerBounds}
        linkRef={drillLinkRef}
        tooltipRef={tooltipRef}
        onMouseEnter={cancelHoverClose}
        onMouseLeave={handleTooltipMouseLeave}
        onRequestClose={() => closeTooltip()}
      />
    </div>
  );
}
